import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../firebase/AuthProvider';
import { searchTasks } from '../services/taskServices/TaskSearchService';

const SEARCH_DEBOUNCE_MS = 250;
const MAX_RESULTS = 10;

export interface ParticipantTaskReference {
    id: string;
    friendlyId: string;
    title: string;
    status?: string;
    participantName?: string;
    organizationAbbreviation: string;
}

/** Searches only client/participant tasks for realtime-chat # references. */
export const useParticipantTaskReferenceSearch = () => {
    const { currentUser, currentOrganization } = useAuth();
    const timerRef = useRef<number>();
    const abortRef = useRef<AbortController>();
    const settleRef = useRef<((tasks: ParticipantTaskReference[]) => void)>();

    useEffect(() => () => {
        window.clearTimeout(timerRef.current);
        abortRef.current?.abort();
        settleRef.current?.([]);
    }, []);

    return useCallback((searchTerm: string): Promise<ParticipantTaskReference[]> => {
        window.clearTimeout(timerRef.current);
        abortRef.current?.abort();
        settleRef.current?.([]);

        const organizationAbbreviation = currentOrganization?.abbreviation?.trim();
        const normalized = searchTerm.trim().replace(/^#/, '');
        const prefixedMatch = normalized.match(/^([A-Za-z0-9]+)-(\d+)$/);
        const isBareFriendlyId = /^\d+$/.test(normalized);
        const prefixMatchesCurrentOrganization = Boolean(
            prefixedMatch &&
            organizationAbbreviation &&
            prefixedMatch[1].toLowerCase() === organizationAbbreviation.toLowerCase(),
        );

        if (!organizationAbbreviation || (!isBareFriendlyId && !prefixMatchesCurrentOrganization)) {
            return Promise.resolve([]);
        }
        const requestedFriendlyId = prefixedMatch?.[2] ?? normalized;

        return new Promise((resolve, reject) => {
            settleRef.current = resolve;
            timerRef.current = window.setTimeout(async () => {
                const controller = new AbortController();
                abortRef.current = controller;
                try {
                    if (!currentUser) return resolve([]);
                    const authToken = await currentUser.getIdToken();
                    if (controller.signal.aborted) return resolve([]);
                    const result = await searchTasks(authToken, {
                        searchTerm: normalized,
                        page: 1,
                        pageSize: MAX_RESULTS,
                    }, controller.signal);
                    const participantTasks = (result?.tasks ?? [])
                        .filter(task => Boolean(task.participant) && String(task.friendlyId) === requestedFriendlyId)
                        .map(task => ({
                            id: String(task.id),
                            friendlyId: String(task.friendlyId),
                            title: task.title,
                            status: typeof task.status === 'string' ? task.status : task.status?.name,
                            participantName: task.participant
                                ? `${task.participant.firstName} ${task.participant.lastName}`.trim()
                                : undefined,
                            organizationAbbreviation,
                        }));
                    resolve(controller.signal.aborted ? [] : participantTasks);
                } catch (error) {
                    if ((error as DOMException)?.name === 'AbortError') resolve([]);
                    else reject(error);
                } finally {
                    if (abortRef.current === controller) abortRef.current = undefined;
                    if (settleRef.current === resolve) settleRef.current = undefined;
                }
            }, SEARCH_DEBOUNCE_MS);
        });
    }, [currentOrganization?.abbreviation, currentUser]);
};
